import React from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { checkAndUpdateOrderProgress } from "../../utils/firebaseUtils";

const GenerationalForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [formData, setFormData] = React.useState<{ [key: string]: string }>({
    yourName: "",
    artTitle: "",
    featuredName: "",
    featuredBirthdate: "",
    featuredAdditionalInfo: "",
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orderId) {
      setSubmitStatus({
        type: "error",
        message:
          "No order ID provided. Please access this form from your order page.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const dataToSubmit = {
        ...formData,
        submittedAt: new Date().toISOString(),
      };

      // BLOCKED: Save form data to Firestore
      console.log(
        "BLOCKED FIRESTORE WRITE: Would save Generational form data for order:",
        orderId
      );
      console.log("Data that would be written:", dataToSubmit);
      // const orderRef = doc(db, "orders", orderId);
      // await updateDoc(orderRef, {
      //   "formData.Generational": dataToSubmit,
      //   "formStatus.Generational": true,
      //   lastFormUpdate: serverTimestamp(),
      // });

      // BLOCKED: Check if this triggers a status update
      console.log(
        "BLOCKED: Would check and update order progress for order:",
        orderId
      );
      // const updatedFormStatus = { Generational: true };
      // await checkAndUpdateOrderProgress(orderId, updatedFormStatus);

      setSubmitStatus({
        type: "success",
        message:
          "Generational information saved successfully! Your order status has been updated.",
      });

      console.log("Form Data Submitted:", dataToSubmit);
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to save form data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{ maxWidth: "900px", margin: "auto", mt: 4, mb: 4 }}
      component="form"
      onSubmit={handleSubmit}
    >
      <Typography variant="h4" gutterBottom>
        Generational Names & Birthdates
      </Typography>
      <Typography variant="body1" paragraph>
        Fill out the appropriate fields which correspond with how many
        generations will be featured in your art. A birthdate and deathdate are
        optional. You may choose other information to share about them, such as
        "Member of the Martin Handcart Company."
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="h6" component="label">
            Your Name*
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            name="yourName"
            variant="outlined"
            required
            value={formData.yourName}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="h6" component="label">
            Title for the top of your Art Piece*
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            name="artTitle"
            variant="outlined"
            required
            value={formData.artTitle}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="h6" component="label">
            Featured Individual's Name*
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            name="featuredName"
            variant="outlined"
            required
            value={formData.featuredName}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="h6" component="label">
            Their birthdate
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            name="featuredBirthdate"
            variant="outlined"
            value={formData.featuredBirthdate}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="h6" component="label">
            Any additional information you want to add
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="featuredAdditionalInfo"
            variant="outlined"
            value={formData.featuredAdditionalInfo}
            onChange={handleInputChange}
          />
        </Grid>

        {[
          "mother/father",
          "grandmother/father",
          "great grandmother/father",
          "great, great grandmother/father",
          "great, great, great grandmother/father",
          "great, great, great, great grandmother/father",
        ].map((relation, index) => (
          <React.Fragment key={index}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="h6" component="label">
                Featured individual's {relation} as applicable
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name={`relation_${index}_name`}
                variant="outlined"
                value={formData[`relation_${index}_name`] || ""}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="h6" component="label">
                Their birthdate
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name={`relation_${index}_birthdate`}
                variant="outlined"
                value={formData[`relation_${index}_birthdate`] || ""}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="h6" component="label">
                Any additional information you want to add
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name={`relation_${index}_additionalInfo`}
                variant="outlined"
                value={formData[`relation_${index}_additionalInfo`] || ""}
                onChange={handleInputChange}
              />
            </Grid>
          </React.Fragment>
        ))}

        <Grid size={{ xs: 12 }}>
          {submitStatus.type && (
            <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
              {submitStatus.message}
            </Alert>
          )}

          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={isSubmitting || !orderId}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? "Saving..." : "Submit Generational Information"}
          </Button>

          {!orderId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please access this form from your order page to ensure proper data
              saving.
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default GenerationalForm;
